import { Sparkles, ArrowRight, SkipForward, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import type { WizardResponses } from './types'

const QUESTIONS: Array<{ field: keyof WizardResponses; question: string; placeholder: string }> = [
  {
    field: 'uniqueSellingPoints',
    question: "What makes your firm stand out?",
    placeholder: "e.g., 30 years experience, no win no fee, 24/7 availability, multilingual staff...",
  },
  {
    field: 'typicalClientConcerns',
    question: "What concerns do callers usually have?",
    placeholder: "e.g., How much will it cost? How long will my case take? Will I have to go to court?",
  },
  {
    field: 'consultationProcess',
    question: "How does your consultation process work?",
    placeholder: "e.g., Free 15-minute phone consultation, then in-person meeting if needed...",
  },
  {
    field: 'feeStructure',
    question: "How should the AI discuss fees?",
    placeholder: "e.g., We work on contingency for PI cases, hourly for family law, free initial consultation...",
  },
  {
    field: 'urgentCaseHandling',
    question: "How do you handle urgent situations?",
    placeholder: "e.g., After-hours emergency line, same-day consultations for arrests or accidents...",
  },
]

interface BuilderPhaseQuestionsProps {
  wizardResponses: WizardResponses
  onUpdate: (field: keyof WizardResponses, value: string) => void
  onContinue: () => void
  onSkipAll: () => void
  saving?: boolean
}

export default function BuilderPhaseQuestions({
  wizardResponses,
  onUpdate,
  onContinue,
  onSkipAll,
  saving,
}: BuilderPhaseQuestionsProps) {
  const answeredCount = QUESTIONS.filter(q => wizardResponses[q.field].trim()).length

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-brand-royal" />
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Help your AI understand your firm
          </p>
        </div>
        <button
          className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
          onClick={onSkipAll}
        >
          <SkipForward className="h-3 w-3" />
          Skip all
        </button>
      </div>
      <p className="text-[11px] text-amber-600 font-medium">
        These answers are fed directly into your AI agent's knowledge. When callers ask about fees, consultations, or your specialties, the agent will respond based on what you enter here.
      </p>

      <div className="space-y-4">
        {QUESTIONS.map((q) => (
          <div key={q.field} className="space-y-2">
            <p className="text-sm font-medium">{q.question}</p>
            <Textarea
              placeholder={q.placeholder}
              value={wizardResponses[q.field]}
              onChange={(e) => onUpdate(q.field, e.target.value)}
              className="min-h-[72px] text-sm resize-none"
            />
          </div>
        ))}
      </div>

      <Button className="w-full" onClick={onContinue} disabled={saving}>
        {saving ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Saving...
          </>
        ) : (
          <>
            {answeredCount > 0 ? `Continue (${answeredCount}/5 answered)` : 'Skip and continue'}
            <ArrowRight className="h-4 w-4 ml-2" />
          </>
        )}
      </Button>
    </div>
  )
}
